import React, { useRef, useState } from 'react';
import { aiApi } from '../../lib/api';
import { cn } from '../../lib/utils';

type NotesEditorProps = {
  label?: string;
  value?: string | null;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
};

type FormattedNotesProps = {
  value?: string | null;
  compact?: boolean;
  tone?: 'default' | 'muted' | 'danger';
  className?: string;
};

const urlPunctuation = /[.,!?;:]+$/;

function splitUrlSuffix(url: string) {
  const href = url.replace(urlPunctuation, '');
  return { href, suffix: url.slice(href.length) };
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const tokenRegex = /\*\*[^*\n]+?\*\*|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s<>)]+/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let tokenIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(text)) !== null) {
    const token = match[0];
    const index = match.index;

    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }

    const key = `${keyPrefix}-${tokenIndex}`;
    const markdownLink = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);

    if (markdownLink) {
      nodes.push(
        <a
          key={key}
          href={markdownLink[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
          onClick={event => event.stopPropagation()}
        >
          {markdownLink[1]}
        </a>
      );
    } else if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(
        <strong key={key} className="font-semibold text-inherit">
          {renderInline(token.slice(2, -2), `${key}-bold`)}
        </strong>
      );
    } else {
      const { href, suffix } = splitUrlSuffix(token);
      nodes.push(
        <React.Fragment key={key}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
            onClick={event => event.stopPropagation()}
          >
            {href}
          </a>
          {suffix}
        </React.Fragment>
      );
    }

    lastIndex = index + token.length;
    tokenIndex += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function renderBlocks(text: string, compact: boolean) {
  const lines = text.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let bulletItems: React.ReactNode[] = [];

  const flushBullets = () => {
    if (!bulletItems.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc space-y-1 pl-5">
        {bulletItems}
      </ul>
    );
    bulletItems = [];
  };

  lines.forEach((line, index) => {
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (bullet) {
      bulletItems.push(
        <li key={`li-${index}`} className="pl-0.5">
          {renderInline(bullet[1], `li-${index}`)}
        </li>
      );
      return;
    }

    flushBullets();

    if (!line.trim()) {
      if (!compact) blocks.push(<div key={`blank-${index}`} className="h-1" />);
      return;
    }

    blocks.push(
      <p key={`p-${index}`} className="break-words">
        {renderInline(line, `p-${index}`)}
      </p>
    );
  });

  flushBullets();
  return blocks;
}

export function FormattedNotes({ value, compact = false, tone = 'default', className }: FormattedNotesProps) {
  const text = (value || '').trim();
  if (!text) return null;

  return (
    <div
      className={cn(
        'whitespace-normal break-words',
        compact ? 'max-h-12 overflow-hidden text-xs leading-5' : 'text-sm leading-6',
        tone === 'danger' && 'text-[#DC2626]',
        tone === 'muted' && 'text-[#71717A]',
        tone === 'default' && 'text-[#3F3F46]',
        className
      )}
    >
      {renderBlocks(text, compact)}
    </div>
  );
}

export function NotesEditor({
  label = 'Notes',
  value,
  onChange,
  rows = 3,
  placeholder,
  error,
  disabled,
  className,
}: NotesEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiModel, setAiModel] = useState<string | null>(null);
  const currentValue = value || '';

  const setNextValue = (nextValue: string, selectionStart?: number, selectionEnd?: number) => {
    onChange(nextValue);
    window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      if (typeof selectionStart === 'number') {
        textarea.setSelectionRange(selectionStart, selectionEnd ?? selectionStart);
      }
    });
  };

  const getSelection = () => {
    const textarea = textareaRef.current;
    return {
      start: textarea?.selectionStart ?? currentValue.length,
      end: textarea?.selectionEnd ?? currentValue.length,
    };
  };

  const insertBold = () => {
    const { start, end } = getSelection();
    const selected = currentValue.slice(start, end) || 'bold text';
    const nextValue = `${currentValue.slice(0, start)}**${selected}**${currentValue.slice(end)}`;
    setNextValue(nextValue, start + 2, start + 2 + selected.length);
  };

  const insertBullet = () => {
    const { start, end } = getSelection();
    const selected = currentValue.slice(start, end);

    if (selected.includes('\n')) {
      const bulletText = selected
        .split('\n')
        .map(line => (line.trim() && !/^\s*[-*]\s+/.test(line) ? `- ${line}` : line))
        .join('\n');
      const nextValue = `${currentValue.slice(0, start)}${bulletText}${currentValue.slice(end)}`;
      setNextValue(nextValue, start, start + bulletText.length);
      return;
    }

    const prefix = start > 0 && currentValue[start - 1] !== '\n' ? '\n- ' : '- ';
    const nextValue = `${currentValue.slice(0, start)}${prefix}${currentValue.slice(start)}`;
    setNextValue(nextValue, start + prefix.length);
  };

  const handleAiImprove = async () => {
    const text = currentValue.trim();
    if (!text) return;
    setAiLoading(true);
    setAiError(null);
    setAiModel(null);
    try {
      const res = await aiApi.improveNotes(text);
      onChange(res.improved);
      setAiModel(res.model || null);
    } catch (err: any) {
      setAiError(err?.message || 'AI improvement failed');
    } finally {
      setAiLoading(false);
    }
  };

  const insertLink = () => {
    const { start, end } = getSelection();
    const selected = currentValue.slice(start, end);
    const selectedIsUrl = /^https?:\/\//.test(selected.trim());
    const text = selected && !selectedIsUrl ? selected : 'link';
    const href = selectedIsUrl ? selected.trim() : 'https://example.com';
    const snippet = `[${text}](${href})`;
    const urlStart = start + text.length + 3;
    const nextValue = `${currentValue.slice(0, start)}${snippet}${currentValue.slice(end)}`;
    setNextValue(nextValue, urlStart, urlStart + href.length);
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label className="block text-sm font-semibold text-[#18181B]">{label}</label>
        <div className="flex items-center gap-1.5">
          {/* AI Improve button */}
          <button
            type="button"
            onClick={handleAiImprove}
            disabled={disabled || aiLoading || !currentValue.trim()}
            title="Convert Roman Urdu → English & improve"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold transition-all disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              background: aiLoading ? '#FFF7ED' : 'linear-gradient(135deg, #F97316, #EA580C)',
              color: aiLoading ? '#F97316' : '#fff',
              border: aiLoading ? '1px solid #FED7AA' : '1px solid transparent',
              boxShadow: aiLoading ? 'none' : '0 1px 4px rgba(249,115,22,0.35)',
            }}
          >
            {aiLoading ? (
              <>
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Improving…
              </>
            ) : (
              <>✨ AI Improve</>
            )}
          </button>

          {/* Formatting toolbar */}
          <div className="flex items-center gap-1 rounded-lg border border-[#E4E4E7] bg-white p-0.5">
            <button
              type="button"
              onClick={insertBold}
              disabled={disabled}
              title="Bold"
              className="flex h-7 w-7 items-center justify-center rounded-md text-sm font-bold text-[#52525B] hover:bg-[#F4F4F5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              B
            </button>
            <button
              type="button"
              onClick={insertBullet}
              disabled={disabled}
              title="Bullet list"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#52525B] hover:bg-[#F4F4F5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
              </svg>
            </button>
            <button
              type="button"
              onClick={insertLink}
              disabled={disabled}
              title="Link"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#52525B] hover:bg-[#F4F4F5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-2 2a4 4 0 01-5.656-5.656l1-1M10.172 13.828a4 4 0 010-5.656l2-2a4 4 0 015.656 5.656l-1 1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {aiError && (
        <p className="mb-1.5 text-xs text-red-500 flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
          {aiError}
        </p>
      )}
      {aiModel && !aiError && (
        <p className="mb-1.5 text-xs text-green-600 flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          Improved via <span className="font-mono font-medium">{aiModel.replace(':free', '')} (free)</span>
        </p>
      )}
      <textarea
        ref={textareaRef}
        value={currentValue}
        onChange={event => onChange(event.target.value)}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full rounded-lg border bg-white px-3 py-2 text-sm text-[#18181B] placeholder-[#A1A1AA]',
          'resize-none transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316]',
          error ? 'border-danger' : 'border-[#E4E4E7]'
        )}
      />
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
      {currentValue.trim() && (
        <div className="mt-2 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2">
          <FormattedNotes value={currentValue} />
        </div>
      )}
    </div>
  );
}
